import { useTheme } from '@mui/material';
import { styled as muiStyled, css } from '@mui/material/styles';
import { ResponsiveLine, LineSvgProps } from '@nivo/line';
import React from 'react';

import SliceTooltip from './graph-tooltip';

import * as GQL from '~/graphql/generated/client';
import Arranger from '~c/components/base/arranger';
import Card from '~c/components/base/card';
import { DesignToken } from '~c/components/base/core';
import { useWindowSize } from '~c/hooks/use-hooks';

const MIN_GRAPH_HEIGHT = 500;

export interface LineGraphT {
  id: string;
  color: string;
  data: RiskValueGraphDatum[];
}

export interface RiskValueGraphDatum {
  x: string | null;
  y: number | null;
  value: string | null | undefined;
  timestamp: string | null | undefined;
}

export const lineColor = [
  DesignToken.status.danger,
  DesignToken.status.warning,
  DesignToken.status.info,
  DesignToken.link,
  DesignToken.brand.primaryContrast,
  DesignToken.status.success,
  '#393d92',
  '#ac22cf',
  DesignToken.textMuted,
  DesignToken.link,
  DesignToken.status.success,
  DesignToken.status.warning,
  DesignToken.risk.high,
];

export const RISK_STATUS = {
  [GQL.AssessmentRiskStatusType.Unavailable]: 0,
  [GQL.AssessmentRiskStatusType.Unverified]: 0,
  [GQL.AssessmentRiskStatusType.Low]: 1,
  [GQL.AssessmentRiskStatusType.Medium]: 2,
  [GQL.AssessmentRiskStatusType.High]: 3,
};

interface Props extends LineSvgProps {
  className?: string;
  title?: string;
}

const RiskOverTimeGraph: React.FC<Props> = ({
  className,
  data,
  title,
}: Props) => {
  const theme = useTheme();
  const { height } = useWindowSize();
  const graphHight = Math.max(MIN_GRAPH_HEIGHT, height / 5);
  const isDarkMode = theme.palette.mode === 'dark';
  const axisColor = theme.palette.text.primary;
  const tooltipBg = theme.palette.background.paper;

  return (
    <div className={className}>
      <Card title={title}>
        <Arranger
          alignItems="center"
          justify="center"
          sx={{ height: graphHight }}
        >
          <ResponsiveLine
            data={data}
            margin={{ top: 30, right: 110, bottom: 50, left: 120 }}
            theme={{
              axis: {
                ticks: {
                  text: {
                    fontSize: 14,
                    fill: axisColor,
                  },
                },
              },
              tooltip: {
                table: {
                  background: tooltipBg,
                },
                container: {
                  background: tooltipBg,
                },
              },
            }}
            xScale={{ type: 'point' }}
            yScale={{
              type: 'linear',
              min: 0,
              max: 3,
            }}
            axisTop={null}
            axisRight={null}
            axisBottom={{
              tickPadding: 15,
            }}
            axisLeft={{
              tickSize: 5,
              tickPadding: 10,
              tickRotation: 0,
              tickValues: Object.values(RISK_STATUS),
              format: (value) => {
                let tick = '';
                if (value === 0) {
                  tick = GQL.AssessmentRiskStatusType.Unavailable;
                } else if (value === 1) {
                  tick = GQL.AssessmentRiskStatusType.Low;
                } else if (value === 2) {
                  tick = GQL.AssessmentRiskStatusType.Medium;
                } else if (value === 3) {
                  tick = GQL.AssessmentRiskStatusType.High;
                }
                return tick;
              },
            }}
            colors={{ datum: 'color' }}
            pointSize={10}
            pointColor={{ theme: 'background' }}
            pointBorderWidth={2}
            pointBorderColor={{ from: 'serieColor' }}
            pointLabelYOffset={-12}
            useMesh
            enableSlices="x"
            enableGridY={false}
            markers={Object.values(RISK_STATUS).map((value) => ({
              axis: 'y',
              legendOrientation: 'vertical',
              lineStyle: {
                stroke: axisColor,
                strokeWidth: 0.3,
              },
              value,
            }))}
            sliceTooltip={(slice) => {
              return <SliceTooltip {...slice} />;
            }}
          />
        </Arranger>
      </Card>
    </div>
  );
};

export default muiStyled(RiskOverTimeGraph)(({ theme }) => {
  return css``;
});
